import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import validate from "../../validate";
import history from "../../history";
import {
  updateUserInProfileSilent,
  updateCvInProfileSilent,
  getAddressList,
  storeAddressList,
} from "../../redux/actions";
import RegistrationStep from "../../components/CandidateJob/RegistrationStep";
import {
  useWorkHistoryState,
  workHistoryConstraints,
} from "./helpers/workHistory";
import {
  usePersonalDetailsState,
  personalDetailsConstraints,
} from "./helpers/personalDetails";
import {
  useTypeOfWorkState,
  typeOfWorkConstraints,
} from "./helpers/typeOfWork";
import { saveCV, validateCvFile } from "./helpers/cv";
import {
  emergencyContactConstraints,
  useEmergencyContactState,
} from "./helpers/emergencyContact";
import {
  demographicsConstraints,
  demographicsSelectOptions,
  useDemographicsState,
} from "./helpers/demographics";
import {
  excludedUmbracoBranches,
  getUTCDate,
  nonChangableTeamCodes,
  toInt,
} from "../../helpers/helpers";
import { getByPath } from "../../helpers/index";
import getNearestBranch from "../../helpers/nearestBranch";
import { uiModelToWorkHistory } from "../../helpers/workHistory";

const findStep = (section, jobId, appData = {}) => {
  const { cv: cvUploaded, workHistory = [] } = appData;
  const hasWorkHistory = workHistory.length > 0;

  if (section === "type-of-work") {
    return hasWorkHistory && !cvUploaded
      ? `/profile/edit/job/${jobId}/registration/work-history`
      : `/profile/edit/job/${jobId}/registration/cv`;
  }

  if (section === "work-history-back") {
    return hasWorkHistory && !cvUploaded
      ? `/profile/edit/job/${jobId}/registration/type-of-work`
      : `/profile/edit/job/${jobId}/registration/cv`;
  }

  switch (section) {
    case "personal-details":
      return `/profile/edit/job/${jobId}/registration/profile-picture`;
    case "profile-picture":
      return `/profile/edit/job/${jobId}/registration/type-of-work`;
    case "always-cv":
      return `/profile/edit/job/${jobId}/registration/cv`;
    case "cv":
    case "work-history":
      return `/profile/edit/job/${jobId}/registration/emergency-contact`;
    case "emergency-contact":
      return `/profile/edit/job/${jobId}/registration/demographics`;
    case "demographics":
      return `/profile/edit/job/${jobId}/legal-compliance/48-hour-opt-out`;
    default:
      return `/profile/edit/job/${jobId}/registration/personal-details`;
  }
};

const JobRegistrationContainer = ({
  appData,
  updateCandidate,
  updateCV,
  jobId,
  section,
  cvLoading,
  getAddressListFromProps,
  responsibleTeamCode,
  branches: umbracoBranches,
  matchedAddressList,
  matchedPostcodePosition,
  postcodeError,
  resetAddressList,
  currentJobProgressState,
}) => {
  const cvUploaded = appData?.cv;
  const workHistories = appData?.workHistory || [];

  const [fileError, setFileError] = useState("");
  const [updateBranch, setUpdateBranch] = useState(false);
  const [branchCode, setBranchCode] = useState(responsibleTeamCode);
  const branches = umbracoBranches.filter(
    (branch) => !excludedUmbracoBranches.includes(branch.code)
  );

  const [
    workHistoryState,
    handleWorkHistoryState,
    addWorkHistory,
    removeWorkHistory,
    workHistoryErrors,
    setWorkHistoryErrors,
  ] = useWorkHistoryState(workHistories);

  const [
    personalDetailsState,
    personalDetailsStateHandlers,
    personalDetailsErrors,
    setPersonalDetailsErrors,
  ] = usePersonalDetailsState(appData, matchedAddressList, resetAddressList);

  const [
    typeOfWorkState,
    handleTypeWork,
    typeOfWorkErrors,
    setTypeOfWorkErrors,
  ] = useTypeOfWorkState(appData);

  const [
    emergencyContactState,
    handleEmergencyContactState,
    emergencyContactErrors,
    setEmergencyContactErrors,
  ] = useEmergencyContactState(appData);

  const [
    demographicsState,
    handleDemographicsState,
    demographicsErrors,
    setDemographicsErrors,
  ] = useDemographicsState(appData);

  const updateCandidateData = (data) => {
    const { candidateRef, emailAddress } = appData;
    const updatedUserData = {
      candidateRef,
      emailAddress,
      ...data,
    };
    new Promise((resolve) => {
      updateCandidate(candidateRef, updatedUserData);
      resolve();
    }).then(() => {
      history.push(findStep(section, jobId, appData));
    });
  };

  const onSubmitWorkHistory = (fieldRefs) => {
    const newWorkHistoryErrors = { ...workHistoryErrors };

    Object.keys(workHistoryState).forEach((index) => {
      const workHistory = workHistoryState[index];
      const errors = validate(workHistory, workHistoryConstraints);
      newWorkHistoryErrors[index] = errors || workHistoryErrors[index];
    });

    if (
      Object.values(newWorkHistoryErrors).every(
        (errors) =>
          Object.keys(errors).length === 0 ||
          Object.values(errors).every((value) => value === "")
      )
    ) {
      const updatedWorkHistories = Object.values(workHistoryState).map(
        (workHistory) => uiModelToWorkHistory(workHistory)
      );

      const updatedUserData = {
        workHistory: updatedWorkHistories,
      };
      updateCandidateData(updatedUserData);
    } else {
      setWorkHistoryErrors(newWorkHistoryErrors);
      const firstInvalidEntryKey = Object.keys(newWorkHistoryErrors).find(
        (key) =>
          Object.values(newWorkHistoryErrors[key]).some(
            (fieldErrors) =>
              Array.isArray(fieldErrors) && fieldErrors.length > 0
          )
      );
      const firstInvalidEntry = newWorkHistoryErrors[firstInvalidEntryKey];

      const firstInvalidFieldKey = Object.keys(firstInvalidEntry).find(
        (fieldKey) => firstInvalidEntry[fieldKey].length > 0
      );

      if (firstInvalidFieldKey) {
        const fieldToScroll =
          fieldRefs.current[firstInvalidEntryKey]?.[firstInvalidFieldKey];
        if (fieldToScroll) {
          fieldToScroll.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  };

  const onSubmitCV = (event) => {
    event.preventDefault();

    const cvFile = event.target.files ? event.target.files[0] : "";
    const validationErrors = validateCvFile(cvFile);
    setFileError(validationErrors);

    if (validationErrors) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      saveCV({
        appData,
        updateCV,
        cvFile,
      });
    }
  };

  const handleCvSubmitted = () => {
    const nextStep = findStep(section, jobId);
    if (workHistories.length === 0 && !cvUploaded) {
      return setFileError("fileAndWorkHistoryMissing");
    }
    if (fileError || (workHistories.length === 0 && !cvUploaded)) {
      setFileError("fileMissing");
      return window.scrollTo({ top: 0, behavior: "smooth" });
    }

    return history.push(nextStep);
  };

  const { findAddress, resetAddressIndex, manualAddress, onChange } =
    personalDetailsStateHandlers;

  const personalDetailsActions = {
    handleSubmitPersonalDetails: (fieldRefs) => {
      const validationErrors = validate(
        personalDetailsState,
        personalDetailsConstraints
      );
      if (validationErrors) {
        setPersonalDetailsErrors(validationErrors);
        const firstInvalidField = Object.keys(validationErrors)[0];
        fieldRefs.current[firstInvalidField]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      if (!validationErrors) {
        const {
          birthDay,
          birthMonth,
          birthYear,
          mobileTelephone,
          firstName,
          lastName,
          addressLine1,
          addressLine2,
          addressLine3,
          county,
          town,
          country,
          postcode,
        } = personalDetailsState;
        const daysInMonth = moment(
          new Date(toInt(birthYear), toInt(birthMonth), 1)
        ).daysInMonth();
        const correctedDay = birthDay === "" ? daysInMonth : birthDay;
        const dateOfBirthFormated = new Date(
          toInt(birthYear),
          toInt(birthMonth),
          toInt(correctedDay)
        );

        const dateOfBirth = getUTCDate(dateOfBirthFormated);

        const updatedUserData = {
          firstName,
          lastName,
          dateOfBirth,
          mobileTelephone,
          responsibleTeam: branchCode,
          address: {
            addressLine1,
            addressLine2,
            addressLine3,
            county,
            town,
            country,
            postcode,
            ...matchedPostcodePosition,
          },
        };

        updateCandidateData(updatedUserData);
      }
    },
    canFindAddress: personalDetailsState.postcode !== "",
    shouldShowAddresses: personalDetailsState.isFindingAddress,
    showManualAddress: () => {
      manualAddress(true);
      findAddress(false);
    },
    hideManualAddress: () => {
      manualAddress(false);
    },
    getMatchedAddress: (e) => {
      e.preventDefault();
      getAddressListFromProps(personalDetailsState.postcode);
      findAddress(true);
      manualAddress(false);
      resetAddressIndex();
    },
  };

  const setPersonalDetailsState = (key, val) => {
    if (key === "postcode" && !nonChangableTeamCodes.includes(branchCode)) {
      setUpdateBranch(true);
    }
    onChange(key, val);
  };

  const handleSubmitTypeOfWork = (fieldRefs) => {
    const validationErrors = validate(typeOfWorkState, typeOfWorkConstraints);
    if (validationErrors) {
      setTypeOfWorkErrors(validationErrors);
      const firstInvalidField = Object.keys(validationErrors)[0];
      fieldRefs.current[firstInvalidField]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    if (!validationErrors) {
      const updatedUserData = {
        positionSeeking: typeOfWorkState.positionSeeking,
        tempWorkSeeking: true,
        permWorkSeeking: false,
        driver: typeOfWorkState.driver === "yes",
        ownCar: typeOfWorkState.ownCar === "yes",
      };
      updateCandidateData(updatedUserData);
    }
  };

  const handleSubmitEmergencyContact = (e) => {
    e.preventDefault();
    const validationErrors = validate(
      emergencyContactState,
      emergencyContactConstraints
    );
    if (validationErrors) {
      setEmergencyContactErrors(validationErrors);
    }
    if (!validationErrors) {
      const updatedUserData = { ...emergencyContactState };
      updateCandidateData(updatedUserData);
    }
  };

  const handleSubmitDemographics = (e) => {
    e.preventDefault();
    const validationErrors = validate(
      demographicsState,
      demographicsConstraints
    );
    if (validationErrors) {
      setDemographicsErrors(validationErrors);
    }
    if (!validationErrors) {
      const updatedUserData = { ...demographicsState };
      updateCandidateData(updatedUserData);
    }
  };

  const handleProfilePictureSubmitted = (file) => {
    const { candidateRef, emailAddress, permissions } = appData;
    const updatedUserData = {
      candidateRef,
      emailAddress,
      profileImageData: file,
      isPecan: permissions.isPecan,
    };
    updateCandidateData(updatedUserData);
  };

  useEffect(() => {
    if (matchedPostcodePosition.lat && updateBranch) {
      setUpdateBranch(false);
      const branch = getNearestBranch(matchedPostcodePosition, branches);
      const responsibleTeam = branch ? branch.code : "";
      setBranchCode(responsibleTeam);
    }
  }, [matchedPostcodePosition.lat, matchedPostcodePosition.long]);

  return (
    <RegistrationStep
      demographicsState={demographicsState}
      demographicsErrors={demographicsErrors}
      handleDemographicsState={handleDemographicsState}
      handleSubmitDemographics={handleSubmitDemographics}
      demographicsSelectOptions={demographicsSelectOptions}
      emergencyContactState={emergencyContactState}
      emergencyContactErrors={emergencyContactErrors}
      handleEmergencyContactState={handleEmergencyContactState}
      handleSubmitEmergencyContact={handleSubmitEmergencyContact}
      cvUploaded={cvUploaded}
      fileValidationError={fileError}
      handleSubmitCV={onSubmitCV}
      handleCvSubmitted={handleCvSubmitted}
      handleProfilePictureSubmitted={handleProfilePictureSubmitted}
      handleWorkHistoryState={handleWorkHistoryState}
      handleSubmitWorkHistory={onSubmitWorkHistory}
      handleAddNewWorkHistory={addWorkHistory}
      handleRemoveWorkHistory={removeWorkHistory}
      workHistoryState={workHistoryState}
      workHistoryErrors={workHistoryErrors}
      personalDetailsActions={personalDetailsActions}
      personalDetailsStateHandlers={personalDetailsStateHandlers}
      matchedAddressList={matchedAddressList}
      personalDetailsState={personalDetailsState}
      setPersonalDetailsState={setPersonalDetailsState}
      personalDetailsErrors={personalDetailsErrors}
      responsibleTeamCode={branchCode}
      postcodeError={postcodeError}
      branches={branches}
      typeOfWorkState={typeOfWorkState}
      handleTypeWork={handleTypeWork}
      typeOfWorkErrors={typeOfWorkErrors}
      handleSubmitTypeOfWork={handleSubmitTypeOfWork}
      cvLoading={cvLoading}
      section={section}
      findStep={findStep}
      jobId={jobId}
      appData={appData}
      updateCandidate={updateCandidate}
      currentJobProgressState={currentJobProgressState}
    />
  );
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
  cvLoading: state.core.isCvLoading,
  matchedAddressList: state.core.addressList.addresses,
  postcodeError: getByPath("core.postcodeError.response.status", state) === 400,
  matchedPostcodePosition: {
    lat: state.core.addressList.latitude,
    long: state.core.addressList.longitude,
  },
  branches: state.core.umbracoContent.branches,
  responsibleTeamCode: state.core.app.responsibleTeam,
});
const mapDispatchToProps = {
  updateCandidate: updateUserInProfileSilent,
  getAddressListFromProps: getAddressList,
  updateCV: updateCvInProfileSilent,
  resetAddressList: storeAddressList,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(JobRegistrationContainer);
